// The incident classifier (Brief 5 / pt10 + pt20) — the PUSH half of the
// recovery-notice surface. Where `deriveSessionStatus` is the pull-view (a digest
// you open to "catch me up"), this is the proactive ALERT: the core UX
// is that a caught error is "caught → bounded → reversed, never cosmetic", so a
// recovery must be SURFACED, not handled silently. This module owns the PURE
// derivation: given a previous + next signal snapshot, classify the transition
// into a single factual incident descriptor (or null when nothing changed worth a
// notice). The stateful accumulation + the rendering live in @waytide/ui.
//
// FACTS ONLY (pt13 — never predict): every descriptor reports what ALREADY
// happened (the connection dropped; the reconnect succeeded; a send is retrying;
// a send was parked; a parked send finally went out). No descriptor guesses an
// outcome ("will reconnect in 3s", "probably a server issue"). Where we can state
// a no-loss guarantee as a FACT we do — a reconnect loses nothing because the SSE
// stream replays, and a queued send is persisted — both are structural, not a
// hopeful prediction.
//
// Plain English (pt11): the `summary` is one calm sentence a non-engineer reads.
//
// Severity is the honesty dial: `recovered` for a
// caught-and-handled transient (calm, auto-dismissable, never the reserved amber)
// vs `needs_input` for the one case that genuinely needs the user (a send the
// auto-retry has GIVEN UP on — it sits with their words until they act). We never
// inflate a transient to `needs_input`, and never bury a real ask as `recovered`.
//
// Pure + UI-free + global-free: no React, no DOM, no clock read. The
// caller passes the prior + current snapshot; this returns a descriptor it can
// stack. Unit-tested directly so the recovered-vs-needs_input-vs-none rules can't
// silently regress.

import type { StreamHealth } from "./sse";

// ─── Severity ─────────────────────────────────────────────────────────────

/** How loudly a notice is allowed to speak — the honesty dial.
 *   - `recovered`  — caught and handled; the system is fine again. CALM: the UI
 *                    auto-dismisses it and NEVER uses the reserved attention
 *                    amber (it is not a "this is on you" call).
 *   - `needs_input`— caught but NOT resolved on its own; it genuinely needs the
 *                    user. The UI keeps it (amber is allowed) until acknowledged. */
export type IncidentSeverity = "recovered" | "needs_input";

/** The stable kind of a classified incident — what the UI keys/dedupes on and
 * can map to an icon/word. Open-ish, but every value is a transition we OBSERVED,
 * never a predicted one. */
export type IncidentKind =
  /** The live stream dropped and is reconnecting (live/connecting → reconnecting). */
  | "connection_dropped"
  /** The stream came back after a drop (reconnecting/connecting → live). */
  | "connection_restored"
  /** The token died — a hard auth failure (→ dead). Needs the user (re-sign-in). */
  | "connection_dead"
  /** A queued send failed and is being auto-retried (still within budget). */
  | "send_retrying"
  /** A queued send finally went out after one or more failed attempts. */
  | "send_recovered"
  /** A queued send exhausted the auto-retry budget and is parked for the user. */
  | "send_parked";

/** A classified incident — the whole input the UI notice needs. Every field is a
 * fact derived from the observed transition; nothing is predicted. */
export interface IncidentDescriptor {
  /** The transition kind (the dedupe/display key). */
  kind: IncidentKind;
  /** How loudly it may speak (drives calm-vs-amber + auto-dismiss-vs-keep). */
  severity: IncidentSeverity;
  /** One calm, plain-English sentence stating what happened (pt11). */
  summary: string;
  /** True when this was caught and HANDLED by the system (a recovery the user
   * didn't have to drive). False for a `needs_input` incident still on the user.
   * Mirrors `CaughtError.handled` in spirit — the fact of the handling. */
  handled: boolean;
  /** True only when we can state, as a FACT, that nothing was lost in the
   * incident (a stream reconnect replays; a queued send is persisted + still
   * queued). False when loss is possible or simply unknown — we never CLAIM
   * "nothing lost" speculatively (pt13). The UI shows the honest no-loss line
   * only where this is true. */
  lost: boolean;
}

// ─── Connection transitions ─────────────────────────────────────────────────

/** The connection facet of a snapshot — just the live transport health. Kept as
 * its own field so the snapshot can grow (an outbox facet rides alongside). */
export interface ConnectionSnapshot {
  health: StreamHealth;
}

/**
 * Classify a connection-health transition into an incident, or null when the
 * transition isn't notice-worthy. FACTS ONLY:
 *   - live/connecting → reconnecting  ⇒ "connection dropped, reconnecting"
 *       (recovered: the reader is already retrying — handled, nothing lost: the
 *       stream replays on reconnect).
 *   - reconnecting    → live          ⇒ "reconnected — nothing was missed"
 *       (recovered: a real recovery to surface; nothing lost is a FACT here).
 *   - connecting      → live          ⇒ null (the FIRST successful connect is not
 *       an incident — there was no prior drop to recover from; a fresh
 *       connection isn't a caught error).
 *   - any             → dead          ⇒ "connection closed — please sign in again"
 *       (needs_input: a hard auth failure the system can't self-heal; `lost` is
 *       false because the queued sends are still persisted, but it is genuinely
 *       on the user now).
 *   - no real change (same health, or a transition we don't narrate) ⇒ null.
 *
 * `prev == null` means "first observation" — only a transition INTO `dead` is
 * worth surfacing from a cold start (a drop/restore needs a prior live baseline
 * to be a real recovery, not a notice minted on mount).
 */
export function deriveConnectionIncident(
  prev: ConnectionSnapshot | null,
  next: ConnectionSnapshot,
): IncidentDescriptor | null {
  const before = prev?.health ?? null;
  const after = next.health;

  // No movement → nothing to say.
  if (before === after) return null;

  // A hard auth failure: the one connection state the system can't self-heal.
  // Surfaced even from a cold start (prev null) — it always needs the user.
  if (after === "dead") {
    return {
      kind: "connection_dead",
      severity: "needs_input",
      summary: "Connection closed — please sign in again.",
      handled: false,
      // The queued sends are persisted + still queued, so nothing typed is lost;
      // but this is genuinely on the user, so it is NOT a calm "nothing lost".
      lost: false,
    };
  }

  // Everything below needs a prior baseline — a drop/restore is only a real
  // recovery against a known-good earlier state, never a mount-time artefact.
  if (before === null) return null;

  // The live feed dropped and the reader is already retrying.
  if (
    after === "reconnecting" &&
    (before === "live" || before === "connecting")
  ) {
    return {
      kind: "connection_dropped",
      severity: "recovered",
      summary: "Connection dropped — reconnecting.",
      handled: true,
      // The stream replays on reconnect, so nothing is lost.
      lost: false,
    };
  }

  // The feed came back after a drop — a genuine recovery to surface calmly.
  if (after === "live" && before === "reconnecting") {
    return {
      kind: "connection_restored",
      severity: "recovered",
      summary: "Reconnected — nothing was missed.",
      handled: true,
      lost: false,
    };
  }

  // The first successful connect (connecting → live) is not an incident: there
  // was no prior drop to recover from. Any other transition we don't narrate.
  return null;
}

// ─── Outbox-send transitions ────────────────────────────────────────────────

/** The minimal facts the classifier needs about ONE queued send, between two
 * observations of it — a UI-free projection of an `OutboxEntry` (the UI maps its
 * real entry onto this; core stays free of the @waytide/ui store). */
export interface OutboxSnapshot {
  /** The send's idempotency/list key — the identity across observations. */
  key: string;
  /** How many delivery attempts have been made so far (0 before the first try). */
  attempts: number;
  /** True once the auto-retry budget is spent and the send is PARKED for a
   * manual retry — the one outbox state that genuinely needs the user. */
  parked: boolean;
}

/**
 * Classify a single queued send's transition between two observations, or null
 * when it isn't notice-worthy. FACTS ONLY:
 *   - present(attempts>0, not parked) AND newly so ⇒ "couldn't reach the server —
 *       retrying your message" (recovered: the auto-retry has it; nothing lost —
 *       the send is persisted + still queued).
 *   - present → gone, having been retried (prev.attempts>0, not parked) ⇒ "your
 *       message went through after a retry" (recovered: a real recovery; the
 *       message landed — nothing lost).
 *   - became parked (prev not parked → next parked) ⇒ "couldn't send your message
 *       — it's saved; tap retry" (needs_input: the auto-retry gave up; the user's
 *       words are SAFE but it's now on them).
 *
 * Disappearance handling: a send vanishes either because it SUCCEEDED (cleared on
 * confirmation) or — not modelled here — was dismissed. We only narrate a
 * disappearance as a recovery when it had been RETRYING (prev.attempts > 0): a
 * send that succeeded on its first attempt was never an incident, so its normal
 * clear is silent (no "recovered from nothing" noise).
 *
 * `prev == null` (first time we see this key) is the enqueue moment. A brand-new
 * send that hasn't failed yet (attempts 0, not parked) is NOT an incident — only
 * a NEW send already observed mid-retry/parked (e.g. re-hydrated from storage
 * after a reload) surfaces, so a crash-recovered backlog is honest on mount.
 */
export function deriveOutboxIncident(
  prev: OutboxSnapshot | null,
  next: OutboxSnapshot | null,
): IncidentDescriptor | null {
  // Gone now — only a recovery if it had been retrying (else it's a normal,
  // never-failed send clearing, which is silent).
  if (next === null) {
    if (prev && prev.attempts > 0 && !prev.parked) {
      return {
        kind: "send_recovered",
        severity: "recovered",
        summary: "Your message went through after a retry.",
        handled: true,
        lost: false,
      };
    }
    return null;
  }

  // Newly parked: the auto-retry budget is spent — genuinely on the user now.
  if (next.parked && !(prev?.parked ?? false)) {
    return {
      kind: "send_parked",
      severity: "needs_input",
      summary: "Couldn't send your message — it's saved. Tap retry to try again.",
      handled: false,
      // The words are persisted + visible, so nothing is lost — but it needs the
      // user, so it is not a calm auto-dismiss; the UI keeps it (amber allowed).
      lost: false,
    };
  }

  // Newly retrying: a send has failed at least once but is still within budget,
  // and we weren't already showing it as retrying.
  const wasRetrying = (prev?.attempts ?? 0) > 0 && !(prev?.parked ?? false);
  const isRetrying = next.attempts > 0 && !next.parked;
  if (isRetrying && !wasRetrying) {
    return {
      kind: "send_retrying",
      severity: "recovered",
      summary: "Couldn't reach the server — retrying your message.",
      handled: true,
      // Persisted + still queued, so the message is safe.
      lost: false,
    };
  }

  return null;
}

// ─── The combined snapshot ───────────────────────────────────────────────────

/** A point-in-time snapshot of the recovery-relevant signals on a surface — the
 * live connection health plus the current queued sends (already projected to the
 * minimal facts). The UI remembers the previous snapshot and diffs it against the
 * next via `deriveIncidents`. */
export interface IncidentSnapshot {
  connection: ConnectionSnapshot;
  /** The queued sends right now, in any order (matched across snapshots by key).
   * Empty when the outbox is clear. */
  outbox: OutboxSnapshot[];
}

/**
 * Diff a previous snapshot against the next and return EVERY incident the
 * transition produced — the connection transition (0 or 1) followed by one per
 * changed send (a newly-retrying, a parked, a recovered-after-retry). PURE +
 * total: a stable snapshot yields `[]`; nothing is predicted.
 *
 * The send diff is by `key`: a key present in both is compared; a key only in
 * `next` is classified against `null` (its first observation — surfaces only if
 * it arrives already retrying/parked, e.g. re-hydrated after a reload); a key
 * only in `prev` is classified against `null`-next (its disappearance — a
 * recovery only if it had been retrying). This keeps a multi-message backlog
 * honest without minting a notice for a fresh, never-failed send.
 *
 * Order: the connection incident first (the transport is the broader fact), then
 * the send incidents in the order their keys appear in `next` (then any
 * disappeared keys), so the UI stacks them deterministically.
 */
export function deriveIncidents(
  prev: IncidentSnapshot | null,
  next: IncidentSnapshot,
): IncidentDescriptor[] {
  const out: IncidentDescriptor[] = [];

  const conn = deriveConnectionIncident(prev?.connection ?? null, next.connection);
  if (conn) out.push(conn);

  const prevByKey = new Map<string, OutboxSnapshot>();
  for (const e of prev?.outbox ?? []) prevByKey.set(e.key, e);

  const seen = new Set<string>();
  for (const e of next.outbox) {
    seen.add(e.key);
    const inc = deriveOutboxIncident(prevByKey.get(e.key) ?? null, e);
    if (inc) out.push(inc);
  }
  // Keys that were present before but are gone now (succeeded or dismissed).
  for (const e of prev?.outbox ?? []) {
    if (seen.has(e.key)) continue;
    const inc = deriveOutboxIncident(e, null);
    if (inc) out.push(inc);
  }

  return out;
}
